import { SongList } from '../../../common/src/model.ts';
import {
  idbClear,
  idbDelete,
  idbGet,
  idbGetAllKeys,
  idbPut,
  idbTestSetup,
  idbTestTeardown,
  SongListTimestamp,
  SongListUpdateObjectStore,
} from './idb.ts';

// - Song updates are handled via a persistent queue
// - Newly updated song lists are added to one end of the queue
// - They are removed from the other end of the queue when successfully uploaded to the network
// - The queue is stored in IndexedDB, which has an async API

let currentTimestamp: SongListTimestamp = 0; // monotonically increasing number

export type DeletableSongList = SongList & { isDeleted?: boolean };

export async function initializeCurrentSongUpdateTimestamp() {
  currentTimestamp = await getMaxTimestamp();
}

export async function testSetup() {
  idbTestSetup();
  await initializeCurrentSongUpdateTimestamp();
}

export async function testTeardown() {
  idbTestTeardown();
  await initializeCurrentSongUpdateTimestamp();
}

async function getMaxTimestamp() {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  return Math.max(...keys.map((key) => key + 1), 0);
}

export async function recordSongListUpdate(songList: DeletableSongList, { isDeleted }: { isDeleted?: boolean } = {}) {
  const timestamp = currentTimestamp++;
  await idbPut(SongListUpdateObjectStore, { ...songList, ...(isDeleted && { isDeleted }) }, timestamp);
}

export function getCurrentSongListUpdateTimestamp(): SongListTimestamp {
  return currentTimestamp;
}

export async function getSongListUpdatesBefore(timestamp: SongListTimestamp): Promise<DeletableSongList[]> {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  let songLists = [] as DeletableSongList[];
  for (const key of keys) {
    if (key >= timestamp) {
      continue;
    }
    const songList = await idbGet(SongListUpdateObjectStore, key);
    if (!songList) {
      continue;
    }
    songLists = [...songLists.filter(({ id }) => id !== songList.id), songList];
  }
  return songLists;
}

export async function removeSongListUpdatesBefore(timestamp: SongListTimestamp) {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  for (const key of keys) {
    if (key >= timestamp) {
      continue;
    }
    await idbDelete(SongListUpdateObjectStore, key);
  }
}

export async function songListUpdatesPending(): Promise<boolean> {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  return keys.length > 0;
}

export async function clearAllSongListUpdates() {
  await idbClear(SongListUpdateObjectStore);
}
